/**
 * <offer-code> and <ship-promise> — the PDP reassurance pair.
 * Merchant request, 2026-08-14.
 *
 * <offer-code>  copies a discount code to the clipboard on tap.
 * <ship-promise> hides the shipping promise when the selected variant is not
 *                available, because "ships in 24 hours" under a sold-out
 *                colourway is a promise the store cannot keep.
 */
(function () {
  class OfferCode extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-offer-code]');
      this.status = this.querySelector('[data-offer-status]');
      this.button?.addEventListener('click', () => this.copy());
    }

    async copy() {
      const code = this.button.dataset.offerCode;
      if (!code) return;

      let ok = false;
      try {
        // navigator.clipboard is undefined on insecure origins, which includes
        // the 127.0.0.1 dev server over http for some browsers. The textarea
        // fallback is not legacy cruft here — it is the path that actually
        // runs while this is being developed.
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(code);
          ok = true;
        } else {
          const el = document.createElement('textarea');
          el.value = code;
          el.setAttribute('readonly', '');
          el.style.cssText = 'position:absolute;left:-9999px';
          document.body.appendChild(el);
          el.select();
          ok = document.execCommand('copy');
          document.body.removeChild(el);
        }
      } catch (e) {
        ok = false;
      }

      this.classList.toggle('is-copied', ok);
      if (this.status) {
        this.status.textContent = ok
          ? window.offerStrings?.copied || 'Code copied'
          : window.offerStrings?.copyFailed || `Copy this code: ${code}`;
      }

      clearTimeout(this.resetTimer);
      this.resetTimer = setTimeout(() => {
        this.classList.remove('is-copied');
        if (this.status) this.status.textContent = '';
      }, 2400);
    }
  }

  class ShipPromise extends HTMLElement {
    connectedCallback() {
      // Bare identifiers: PUB_SUB_EVENTS is a top-level `const`, so it is a
      // global lexical binding and NOT a property of window. See the same note
      // in component-sticky-atc.js — this is the second place it matters.
      const events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
      if (typeof subscribe !== 'function' || !events?.variantChange) return;

      this.unsubscribe = subscribe(events.variantChange, ({ data }) => {
        if (String(data?.sectionId) !== String(this.dataset.sectionId)) return;
        this.hidden = !data?.variant || data.variant.available === false;
      });
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }
  }

  if (!customElements.get('offer-code')) customElements.define('offer-code', OfferCode);
  if (!customElements.get('ship-promise')) customElements.define('ship-promise', ShipPromise);
})();
